import {
  formatCpfCnpj,
  formatPhone,
  normalizeCpfCnpj,
  normalizePhone,
} from '@/utils/formatters';
import { getAuthSession, handleUnauthorizedSession } from '@/services/authService';
import { formatApiDate } from '@/utils/dateTime';

const buildApiUrl = (path) => {
  const normalizedBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${normalizedBaseUrl}${normalizedPath}`;
};

const parseResponseBody = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    return { message: text };
  }
};

const resolveStatusLabel = (item) => {
  const status = String(item?.status || '').trim().toLowerCase();

  if (Number(item?.ativo) === 1) {
    return 'Ativo';
  }

  if (status === 'bloqueado') {
    return 'Bloqueado';
  }

  if (Number(item?.em_analise) === 1 || status.includes('analise')) {
    return 'Em análise';
  }

  return 'Inativo';
};

const buildSearchParams = (filters = {}) => {
  const params = new URLSearchParams();

  const normalizedFilters = {
    page: filters.page,
    limit: filters.limit,
    busca: filters.busca?.trim(),
    id: filters.id?.trim(),
    cpf_cnpj: normalizeCpfCnpj(filters.cpf_cnpj || ''),
    email: filters.email?.trim(),
    telefone: normalizePhone(filters.telefone || ''),
    nomeTipoUsuario: filters.nomeTipoUsuario?.trim(),
    status: filters.status,
    ativo: filters.ativo,
    criadoEmInicio: filters.criadoEmInicio,
    criadoEmFim: filters.criadoEmFim,
  };

  Object.entries(normalizedFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });

  return params.toString();
};

const mapUserRecord = item => ({
  id: item.id,
  cpfCnpj: formatCpfCnpj(item.cpf_cnpj || ''),
  email: item.email || '',
  phone: formatPhone(item.telefone || ''),
    role: item.nomeTipoUsuario || 'Não informado',
  status: resolveStatusLabel(item),
  statusRaw: item.status || '',
  ativo: Number(item.ativo) === 1 ? 1 : 0,
  createdAt: formatApiDate(item.criado_em),
  createdAtRaw: item.criado_em || '',
});

const getAuthToken = () => {
  const session = getAuthSession();
  const token = session?.token;

  if (!token) {
    handleUnauthorizedSession();
    throw new Error('Sessão expirada. Faça login novamente.');
  }

  return token;
};

export const createUser = async (values = {}) => {
  const token = getAuthToken();
  const payload = {
    cpf_cnpj: normalizeCpfCnpj(values.cpf_cnpj || ''),
    senha: values.senha || '',
    email: values.email?.trim() || undefined,
    telefone: normalizePhone(values.telefone || '') || undefined,
    idTipoUsuario: values.idTipoUsuario ? Number(values.idTipoUsuario) : undefined,
    status: values.active ? 'ativo' : 'inativo',
    ativo: values.active ? 1 : 0,
    em_analise: 0,
  };

  const response = await fetch(buildApiUrl('/usuarios/cadastro'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedSession();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    throw new Error(
      responseBody?.mensagem || responseBody?.message || 'Não foi possível criar o usuário.',
    );
  }

  return responseBody;
};

export const listUsers = async (filters = {}) => {
  const token = getAuthToken();
  const search = buildSearchParams(filters);
  const query = search ? `?${search}` : '';

  const response = await fetch(buildApiUrl(`/usuarios${query}`), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedSession();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    throw new Error(
      responseBody?.mensagem || responseBody?.message || 'Não foi possível carregar os usuários.',
    );
  }

  return {
    records: (responseBody?.dados || []).map(mapUserRecord),
    pagination: responseBody?.paginacao || null,
  };
};

export const toggleUserActive = async (id) => {
  const token = getAuthToken();

  const response = await fetch(buildApiUrl(`/usuarios/${id}/ativo`), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedSession();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    throw new Error(
      responseBody?.mensagem
      || responseBody?.message
      || 'Não foi possível atualizar o status do usuário.',
    );
  }

  return responseBody;
};

export const changeUserPassword = async (id, password) => {
  const token = getAuthToken();
  const response = await fetch(buildApiUrl(`/usuarios/${id}/senha`), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ senha: password }),
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedSession();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    throw new Error(
      responseBody?.mensagem
      || responseBody?.message
      || 'Não foi possível atualizar a senha do usuário.',
    );
  }

  return responseBody;
};

export const fetchAuthenticatedUser = async () => {
  const token = getAuthToken();

  const response = await fetch(buildApiUrl('/usuarios/validartoken'), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedSession();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    throw new Error(
      responseBody?.mensagem
      || responseBody?.message
      || 'Não foi possível carregar os dados do usuário autenticado.',
    );
  }

  return responseBody;
};

export const uploadUserImage = async (id, file) => {
  if (!file) {
    throw new Error('Selecione uma imagem para enviar.');
  }

  const token = getAuthToken();
  const formData = new FormData();
  formData.append('img', file);

  const response = await fetch(buildApiUrl(`/usuarios/${id}/img`), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedSession();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    throw new Error(
      responseBody?.mensagem
      || responseBody?.message
      || 'Não foi possível atualizar a imagem do usuário.',
    );
  }

  return responseBody;
};
